import Button from "components/Button";
import { Typography } from "@mui/material";
import InfoIcon from "components/SVGs/Info";
import { IClass } from "features/Classes";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useRef, useState } from "react";
import StudentRecord from "./StudentRecord";
import { IValidationErrors } from "features/common/Interfaces";
import {
  includesTwice,
  removeExtraEmptyRecord,
  validate,
} from "./utils/validate";
import { useAddStudentMutation } from "app/services/students";
import { IStudent } from "features/Students";
import useToast from "hooks/useToast";
import Loader from "components/Loader";
import { FireBaseContext } from "contexts/FireBase";

const customStyles = {
  root: {
    direction: "rtl",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "580px",
    padding: "20px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "17px",
    overflowY: "auto",
    overflowX: "hidden",
    alignItems: "center",
    justifyContent: "flex-start",
    "& > :not(:last-child)": {
      padding: "10px 0",
    },
  },
  recordList: {
    height: "350px",
    overflowY: "scroll",
    width: "100%",
    "& > form > div> div  > p > span": {
      // whiteSpace: "nowrap",
      lineHeight: 1,
    },
    "& > *": {
      padding: "15px",
    },
  },
};

interface IProps {
  onSubmit(): void;
  classDetails: IClass | null;
}
export interface IStudentRecord {
  name: string;
  studentId: string;
  email?: string;
}
export interface IRecords {
  data: IStudentRecord;
  errors: IValidationErrors;
  isValid: boolean;
}
export const initialRecord: IRecords = {
  data: {
    name: "",
    studentId: "",
  },
  errors: {},
  isValid: true,
};

const AddingIndividually = ({ onSubmit, classDetails }: IProps) => {
  const { logEvent } = useContext(FireBaseContext);
  const { t, i18n } = useTranslation();
  const [records, setRecords] = useState<IRecords[]>([
    JSON.parse(JSON.stringify(initialRecord)) as IRecords,
  ]);
  const [isRecordsNotValid, setIsRecordsNotValid] = useState(false);
  const [addStudent, { isLoading }] = useAddStudentMutation();
  const toast = useToast();
  const listRef = useRef(null);

  useEffect(() => {
    const isFormNotValid =
      records.map((record) => record.isValid).includes(false) ||
      (records.length === 1 && !records[0].data.name.length) ||
      (!records[records.length - 1].data.name.length &&
        records[records.length - 1].data.studentId.length > 0);
    if (!isFormNotValid) {
      const lastRecord = records[records.length - 1]?.data;
      if (lastRecord?.name?.length) {
        setRecords([
          ...records,
          JSON.parse(JSON.stringify(initialRecord)) as IRecords,
        ]);
      }
    }
    setIsRecordsNotValid(isFormNotValid);
    if (listRef?.current)
      (listRef.current as HTMLDivElement).scroll({
        top: 300 + records.length * 50,
        behavior: "smooth",
      });
  }, [JSON.stringify(records)]);

  const getRecord = (data: IStudentRecord, index: number) => {
    let updatedRecords: IRecords[] = structuredClone(records);
    let alreadyInUseStudentId =
      records[index].data.studentId.trim() === data.studentId.trim();
    updatedRecords[index].data = data;
    //errors
    updatedRecords[index].errors = validate(data, {
      ...(alreadyInUseStudentId ? updatedRecords[index].errors : {}),
    });
    // the case if name is empty and studentId is not empty, an error message that name is required
    // but only if it is not the last record becuase once it is we will wait until it is filled by user
    if (
      (data.studentId.length || data.email?.length) &&
      !data.name.length &&
      index !== records.length - 1
    )
      updatedRecords[index].errors = {
        ...updatedRecords[index].errors,
        name: t("Student Name is required"),
      };
    // the case if studentId duplicated, traversal all records to add this error
    let updateRows = checkDupIds(updatedRecords);

    // Validation
    updateRows[index].isValid = !(
      Object.keys(updateRows[index].errors).length > 0
    );

    updateRows = removeExtraEmptyRecord(updateRows, initialRecord);
    setRecords(updateRows);
  };
  function checkDupIds(data: IRecords[]) {
    const updatedRecords = structuredClone(data);
    const ids = updatedRecords.map((record) => record.data.studentId);
    let duplicatedKeys: number[] = [];
    ids.forEach((id) => {
      if (id) {
        const { count, indexes } = includesTwice(ids, id);
        if (count > 1)
          duplicatedKeys = Array.from(
            new Set<number>([...duplicatedKeys, ...indexes])
          );
      }
    });

    duplicatedKeys.forEach((i) => {
      const currentRecordErrors = updatedRecords[i].errors;
      if (!currentRecordErrors.studentId?.length)
        currentRecordErrors.studentId = t("duplicate studentId");
    });

    updatedRecords.forEach((rec, key) => {
      if (
        !duplicatedKeys.includes(key) &&
        rec.errors.studentId === t("duplicate studentId")
      )
        delete rec.errors["studentId"];
    });

    return updatedRecords;
  }

  const removeRecord = (index: number) => {
    let updateRecords = JSON.parse(JSON.stringify(records)) as IRecords[];
    updateRecords.splice(index, 1);
    if (!updateRecords.length)
      updateRecords = [JSON.parse(JSON.stringify(initialRecord)) as IRecords];
    updateRecords = checkDupIds(updateRecords);
    setRecords(updateRecords);
  };

  const createHandler = async () => {
    try {
      const recordsInfo = JSON.parse(JSON.stringify(records)) as IRecords[];
      recordsInfo.pop();
      const studentsInfo: Partial<IStudent>[] = recordsInfo.map((rec) => ({
        name: rec.data.name,
        studentId: rec.data.studentId,
        classId: classDetails?.id,
        email: rec.data.email,
      }));
      const data = await addStudent(studentsInfo).unwrap();
      if (data._statusCode === 200) {
        toast({
          label: t("success"),
          message: t("Students Added"),
          rtl: i18n.dir() === "rtl",
          type: "success",
          toastId: "studentsAdd",
        });
        onSubmit();
      } else if (data._statusCode === 400) {
        toast({
          label: t("error"),
          message: t("Students Not Added"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "studentsAdd",
        });
      } else if (data._statusCode === 572) {
        toast({
          label: t("error"),
          message: t("studentId already exists"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "studentsAdd",
        });
        const dupIds = data._entity as string[];
        const updatedRecords = JSON.parse(
          JSON.stringify(recordsInfo)
        ) as IRecords[];
        updatedRecords.forEach((rec) => {
          if (dupIds.includes(rec.data.studentId)) {
            rec.errors["studentId"] = t("studentId already exists");
            rec.isValid = false;
          }
        });
        setRecords(updatedRecords);
      } else throw data;
    } catch (error) {
      console.log(error);
    }
    logEvent("ClickOnCreateStudent", { Creation_Type: "Individually" });
  };

  return (
    <>
      <Loader open={isLoading} />
      <Typography component="div" sx={customStyles.root} tabIndex={-1}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            flexDirection: "row",
            width: "100%",
            direction: i18n.dir(),
          }}
        >
          <InfoIcon />
          <Typography sx={{ color: "rgb(105 123 140)", fontSize: "16px" }}>
            {t("To add anther student press enter")}
          </Typography>
        </div>
        <Typography component="div" sx={customStyles.recordList} ref={listRef}>
          {records.map((record, index) => (
            <StudentRecord
              data={record.data}
              validationErrors={record.errors}
              getRecord={getRecord}
              index={index}
              removeRecord={removeRecord}
              noLabel={index > 0}
              key={JSON.stringify(record) + index}
            />
          ))}
        </Typography>
        <Button
          label={t("Create students")}
          action={createHandler}
          styles={{ width: "300px", margin: "35px 0" }}
          disable={isRecordsNotValid || isLoading}
        />
      </Typography>
    </>
  );
};

export default AddingIndividually;
